import { get, post } from '../baseService'
import { UserModel } from 'app/models/userModel'
import { UserInfoModel } from 'app/models/userInfoModel'
import { RemoveUserRoleModel } from 'app/models/removeUserRoleModel'
import { RemoveUserFromCompanyModel } from 'app/models/removeUserFromCompany'
import { SetUserPasswordModel } from 'app/models/user/setUserPasswordModel'
import { KeyInputModel } from 'app/models/keyModel'

type UserMethods = {
  getUserInfo: (userId?: string, companyId?: string) => Promise<any>
  getUsers: (
    companyId?: string,
    tenantId?: string,
    take?: number,
    skip?: number,
    orderBy?: string,
    descending?: boolean,
    searchValue?: string,
  ) => Promise<any>
  getPagedUsers: (
    companyId?: string,
    tenantId?: string,
    take?: number,
    skip?: number,
    orderBy?: string,
    descending?: boolean,
    searchValue?: string,
    onlyBlocked?: boolean,
  ) => Promise<any>
  createUser: (user: UserModel, handleErrorInternally?: boolean) => Promise<any>
  removeUserRole: (
    request: RemoveUserRoleModel,
    handleErrorInternally?: boolean,
  ) => Promise<any>
  editUserProfile: (
    user: UserInfoModel,
    handleErrorInternally?: boolean,
  ) => Promise<any>
  deleteUsers: (ids: Array<string>) => Promise<any>
  createServiceUser: (
    user: KeyInputModel,
    handleErrorInternally?: boolean,
  ) => Promise<any>
  setUserPassword: (userPassword: SetUserPasswordModel) => Promise<any>
  editUser: (user: UserModel, handleErrorInternally?: boolean) => Promise<any>
  sendInvitationEmail: (
    user: string,
    handleErrorInternally?: boolean,
  ) => Promise<any>
  unlockUser: (id: string) => Promise<any>
  removeFromCompany: (
    request: RemoveUserFromCompanyModel,
    handleErrorInternally?: boolean,
  ) => Promise<any>
}

const create = (): UserMethods => {
  const getUserInfo = (
    userId?: string,
    companyId?: string,
    handleErrorInternally: boolean = true,
  ) => {
    let url = '/config-api/user'
    if (userId != null && companyId != null) {
      url = `/config-api/user?userId=${userId}&companyId=${companyId}`
    } else if (companyId != null) {
      url = `/config-api/user?companyId=${companyId}`
    } else if (userId != null) {
      url = `/config-api/user?userId=${userId}`
    }

    return get(url, handleErrorInternally)
  }

  const getUsers = (companyId?: string, tenantId?: string) => {
    const url =
      companyId == null
        ? `/config-api/user/list`
        : `/config-api/user/list?companyId=${companyId}&tenantId=${tenantId == null ? '' : tenantId}`
    return get(url)
  }

  const getPagedUsers = (
    companyId?: string,
    tenantId?: string,
    take?: number,
    skip?: number,
    orderBy?: string,
    descending?: boolean,
    searchValue?: string,
    onlyBlocked?: boolean,
  ) => {
    const url =
      companyId == null
        ? `/config-api/user/list?paged=true&take=${take}&skip=${skip}&orderBy=${orderBy}&descending=${descending}&searchValue=${searchValue}&onlyBlocked=${onlyBlocked != null ? onlyBlocked : false}`
        : `/config-api/user/list?paged=true&companyId=${companyId}&tenantId=${tenantId == null ? '' : tenantId}&take=${take}&skip=${skip}&orderBy=${orderBy}&descending=${descending}&searchValue=${searchValue}&onlyBlocked=${onlyBlocked != null ? onlyBlocked : false}`
    return get(url)
  }

  const createUser = (user: UserModel, handleErrorInternally: boolean = true) =>
    post('/config-api/user/createUser', user, handleErrorInternally)
  const createServiceUser = (
    user: KeyInputModel,
    handleErrorInternally: boolean = true,
  ) =>
    post(
      '/config-api/user/createServiceUser',
      user,
      handleErrorInternally,
    )
  const removeUserRole = (
    request: RemoveUserRoleModel,
    handleErrorInternally: boolean = true,
  ) =>
    post(
      '/config-api/user/removeUserRole',
      request,
      handleErrorInternally,
    )
  const removeFromCompany = (
    request: RemoveUserFromCompanyModel,
    handleErrorInternally: boolean = true,
  ) =>
    post(
      '/config-api/user/removeFromCompany',
      request,
      handleErrorInternally,
    )
  const editUserProfile = (
    user: UserInfoModel,
    handleErrorInternally: boolean = true,
  ) =>
    post(
      '/config-api/user/editUserProfile',
      user,
      handleErrorInternally,
    )
  const editUser = (user: UserModel, handleErrorInternally: boolean = true) =>
    post('/config-api/user/editUser', user, handleErrorInternally)

  const setUserPassword = (
    user: SetUserPasswordModel,
    handleErrorInternally: boolean = true,
  ) =>
    post(
      '/config-api/user/updatePassword',
      user,
      handleErrorInternally,
    )
  const sendInvitationEmail = (
    login: string,
    handleErrorInternally: boolean = true,
  ) =>
    post(
      '/config-api/user/sendInvitationEmail',
      { login },
      handleErrorInternally,
    )
  const unlockUser = (id: string, handleErrorInternally: boolean = true) =>
    post('/config-api/user/unlockUser', { id }, handleErrorInternally)
  const deleteUsers = (ids: string[]) =>
    Promise.all(
      ids.map((id) => post('/config-api/user/delete', { id }, false)),
    )

  return {
    getUserInfo,
    getUsers,
    createUser,
    editUserProfile,
    removeUserRole,
    deleteUsers,
    removeFromCompany,
    editUser,
    setUserPassword,
    createServiceUser,
    sendInvitationEmail,
    unlockUser,
    getPagedUsers,
  }
}

export default {
  create,
}
